import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';
import { fetchPageData } from './services/api';
import './App.css';

// Lazy Loaded Pages
const Home = lazy(() => import('./pages/Home'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetails = lazy(() => import('./pages/BlogDetails'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const LocationDetails = lazy(() => import('./pages/LocationDetails'));
const AllLocations = lazy(() => import('./pages/AllLocations'));
const LandAcquisition = lazy(() => import('./services/LandAcquisition'));
const Leasing = lazy(() => import('./services/Leasing'));
const Compliance = lazy(() => import('./services/Compliance'));
const BTSSolutions = lazy(() => import('./services/BTSSolutions'));
const PreLeasedAssets = lazy(() => import('./services/PreLeasedAssets'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/Terms & Conditions'));
const Projects = lazy(() => import('./pages/Projects'));
const Careers = lazy(() => import('./pages/Careers'));

const LoadingFallback = () => (
  <div className="page-loader">
    <div className="spinner"></div>
  </div>
);


// Global Full-Screen Loader Component
const GlobalLoader = ({ isVisible }) => (
  <div className={`global-page-loader ${!isVisible ? 'fade-out' : ''}`}>
    <div className="spinner"></div>
  </div>
);

// Handles Scroll to Top and triggers Global Loading on route change
function RouteChangeHandler({ setIsLoading }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Show loader on route change
    setIsLoading(true);
    // Hide loader after a short delay to simulate page load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 150); // 600ms loading animation
    return () => clearTimeout(timer);
  }, [pathname, setIsLoading]);

  return null;
}



function App() {
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  useEffect(() => {
    // Pre-fetch all pages so they load INSTANTLY when clicked
    const prefetchPages = async () => {
      const pagesToPrefetch = [
        'home', 'about-us', 'careers', 
        'land-acquisition', 'leasing', 
        'compliance', 'bts-solutions', 'pre-leased-assets'
      ];
      for (const slug of pagesToPrefetch) {
        // Fetch quietly in background
        fetchPageData(slug).catch(() => {});
      }
    };
    // Wait a little bit before prefetching so it doesn't block initial load
    setTimeout(prefetchPages, 1000);
  }, []);

  return (
    <Router>
      <RouteChangeHandler setIsLoading={setIsRouteLoading} />
      <div className="App">
        <GlobalLoader isVisible={isRouteLoading} />
        <Header />
        <main>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetails />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<NotFound />} />
              
              {/* ✅ Search Results Page */}
              <Route path="/search" element={<SearchResults />} />
              
              {/* Property & Location Pages */}
              <Route path="/locations" element={<AllLocations />} />
              <Route path="/location/:slug" element={<LocationDetails />} />
              <Route path="/location/:id" element={<LocationDetails />} />
              <Route path="/LocationDetails" element={<LocationDetails />} />
              
              {/* Services Subpages */}
              <Route path="/services/land-acquisition" element={<LandAcquisition />} />
              <Route path="/services/leasing" element={<Leasing />} />
              <Route path="/services/compliance" element={<Compliance />} />
              <Route path="/services/bts-solutions" element={<BTSSolutions />} />
              <Route path="/services/pre-leased-assets" element={<PreLeasedAssets />} />
              
              {/* ✅ Privacy Policy and Terms & Conditions */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              
              {/* ✅ Add routes for individual property/location/blog pages */}
              <Route path="/blog/:id" element={<BlogDetails />} />
            </Routes>
          </Suspense>
        </main>
        <FloatingButtons />
        <Footer />
      </div>
    </Router>
  );
}

export default App;